import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { HomeService } from '../../../../services/home.service';
import { Stockitem } from '../../../../interfaces/stockitem';
import { UserService } from '../../../../services/user';
import { CartService } from '../../../../services/cart';
import { WarehouseTypeService } from '../../../../services/warehouse-type';

import Swal from 'sweetalert2';

const WAREHOUSE_ID_KEY = 'posId';
const LANGUAGE_KEY = 'language';
const LOW_STOCK_THRESHOLD = 10;
const EXPIRATION_WARNING_MONTHS = 3;
const IMAGE_BASE_URL = 'https://backendproduction.blueboxpet.com/storage/app/public/';

const CATEGORY_NAMES: Record<string, string> = {
  '0': 'soft food',
  '1': 'soft food can',
  '2': 'dry food',
  '3': 'bird food',
  '4': 'medicine',
  '5': 'supplement',
  '6': 'accessories',
  '7': 'toy',
  '8': 'grooming',
  '9': 'house',
  '10': 'care',
  '11': 'treats',
  '12': 'training pads',
  '13': 'bags',
  '14': 'cleaning tools',
  '15': 'litter'
};

const PET_TYPE_NAMES: Record<string, string> = {
  '0': 'adult',
  '1': 'kitten',
  '2': 'sterilized',
  '3': 'persian',
  '4': 'puppy',
  '5': 'birds',
  '6': 'other'
};

const PET_NAMES: Record<string, string> = {
  '0': 'cats',
  '1': 'dogs',
  '2': 'birds',
  '3': 'other',
  '4': 'cats&dogs'
};

interface PriceLine {
  key: string;
  label: string;
  value: number;
}

@Component({
  selector: 'app-stockpagemobile',
  templateUrl: './stockpagemobile.component.html',
  styleUrl: './stockpagemobile.component.scss'
})
export class StockpagemobileComponent implements OnInit, OnDestroy {

  public allData: any[] = [];
  public totalItems = 0;
  public stockTotal: any;

  public contentReady = true;
  public noData = false;
  public searched = false;
  public mostSales = false;
  public isStockTaking = false;
  public isLoadingMore = false;

  public showRejectPopUp = false;
  public cartPopUp = false;
  public addImage = false;

  public quantity?: string;
  public itemId?: string | number;
  public stockImageId!: any;

  public imagePreview: string | null = null;
  public selectedImageFile: File | null = null;
  public isUploadingImage = false;

  public accountType!: string;
  public isAdmin = false;
  public isSubAdmin = false;
  public isSuperAdmin = false;

  public searchKey = '';

  public cartItems: any[] = [];
  public cartItemCount = 0;

  public page = 1;
  public currentPage = 1;

  public isAr = localStorage.getItem(LANGUAGE_KEY) === 'ar';
  public warehouseId!: any;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private homeService: HomeService,
    private userService: UserService,
    private cartService: CartService,
    public warehouseTypeService: WarehouseTypeService
  ) { }

  ngOnInit(): void {
    this.warehouseId = localStorage.getItem(WAREHOUSE_ID_KEY);

    this.loadStoredCart();
    this.setupCartSubscriptions();
    this.loadUserPermissions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadStoredCart(): void {
    const storedCart = localStorage.getItem('cart');

    if (storedCart) {
      this.cartItems = JSON.parse(storedCart);
      this.cartItemCount = this.cartItems.length;
    }
  }

  private setupCartSubscriptions(): void {
    this.cartService.cart$
      .pipe(takeUntil(this.destroy$))
      .subscribe(cart => {
        this.cartItems = cart;
      });

    this.cartService.cartCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.cartItemCount = count;
      });
  }

  private loadUserPermissions(): void {
    this.userService.getAccountType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.accountType = data['user_type'];

          this.isSuperAdmin = this.accountType === '10';
          this.isAdmin = this.accountType === '1' || this.accountType === '10';
          this.isSubAdmin = this.accountType === '6';

          if (this.warehouseId !== null && this.warehouseId !== undefined) {
            this.getwarehouse(this.warehouseId, 1);
          } else {
            this.setNoDataState();
          }
        },
        error: (error) => this.handleError(error),
      });
  }

  private setLoadingState(loading: boolean): void {
    this.contentReady = loading;
  }

  private setNoDataState(): void {
    this.noData = true;
    this.contentReady = false;
    this.isLoadingMore = false;
  }

  private updateListData(data: Stockitem[], total?: number, lastPage?: number, append = false): void {
    this.allData = append ? [...this.allData, ...data] : data;

    if (total !== undefined) {
      this.stockTotal = total;
    }

    if (lastPage !== undefined) {
      this.totalItems = Number(lastPage);
    }

    this.noData = this.allData.length === 0;
    this.contentReady = false;
    this.isLoadingMore = false;
  }

  private resetSearchFlags(): void {
    this.searched = false;
    this.mostSales = false;
  }

  private handleError(error: any): void {
    console.error(error);
    this.contentReady = false;
    this.isLoadingMore = false;
  }

  resetPagination(): void {
    this.page = 1;
    this.currentPage = 1;
  }

  getwarehouse(id: any, page: any): void {
    this.warehouseId = id;
    this.currentPage = Number(page);
    this.page = Number(page) + 1;

    this.isStockTaking = false;
    this.setLoadingState(true);
    this.resetSearchFlags();

    this.homeService.getoneWareHouse(id, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateListData(
            data.stocks['data'],
            data.stocks['total'],
            data.stocks['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  private loadMoreWarehouse(page: number): void {
    this.isLoadingMore = true;

    this.homeService.getoneWareHouse(this.warehouseId, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateListData(
            data.stocks['data'],
            data.stocks['total'],
            data.stocks['last_page'],
            true
          );

          this.page = page + 1;
        },
        error: (error) => this.handleError(error),
      });
  }

  getwarehouseBytopSale(page: any): void {
    if (this.isSubAdmin) return;

    this.isStockTaking = false;
    this.searched = false;
    this.mostSales = true;
    this.currentPage = Number(page);
    this.page = Number(page) + 1;

    this.setLoadingState(true);

    this.homeService.getStocksByWarehouse(this.warehouseId, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateListData(
            data['data'],
            data['total'],
            data['last_page']
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  private loadMoreTopSales(page: number): void {
    this.isLoadingMore = true;

    this.homeService.getStocksByWarehouse(this.warehouseId, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateListData(
            data['data'],
            data['total'],
            data['last_page'],
            true
          );

          this.page = page + 1;
        },
        error: (error) => this.handleError(error),
      });
  }

  stockTaking(): void {
    this.setLoadingState(true);
    this.searched = false;
    this.mostSales = false;

    this.homeService.getSimpleStockByWarehouse(this.warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stocks) => {
          this.isStockTaking = true;
          this.updateListData(stocks, stocks.length, 1);
        },
        error: (error) => this.handleError(error),
      });
  }

  clearSearch(): void {
    this.searchKey = '';
    this.searched = false;
    this.mostSales = false;
    this.isStockTaking = false;
    this.resetPagination();
    this.getwarehouse(this.warehouseId, 1);
  }

  search(): void {
    const term = this.searchKey.trim();

    if (!term) {
      this.clearSearch();
      return;
    }

    if (term.length < 3) {
      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'يجب كتابة 3 حروف على الأقل للبحث' : 'Please type at least 3 characters',
        confirmButtonText: this.isAr ? 'حسناً' : 'OK'
      });
      return;
    }

    this.performSearch(false);
  }

  onEnterPressed(): void {
    this.barcodeSearch();
  }

  barcodeSearch(): void {
    this.performSearch(true);
  }

  private performSearch(addToCartFirst = false): void {
    const term = this.searchKey.trim();

    if (!term || !this.warehouseId) return;

    this.setLoadingState(true);
    this.searched = true;
    this.mostSales = false;
    this.isStockTaking = false;

    this.homeService.searchByNameAndWarehouse(term, this.warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          const results = Array.isArray(data) ? data : [];

          this.updateListData(results, results.length, 1);

          if (addToCartFirst && results[0]) {
            this.addToCart(results[0]);
          }
        },
        error: (error) => this.handleError(error),
      });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    if (
      this.contentReady ||
      this.isLoadingMore ||
      this.searched ||
      this.isStockTaking ||
      this.noData
    ) {
      return;
    }

    const scrollPosition = window.scrollY || document.documentElement.scrollTop || 0;
    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    const bottomReached = scrollPosition + windowHeight >= pageHeight - 140;

    if (!bottomReached) return;
    if (this.page > this.totalItems) return;

    if (this.mostSales) {
      this.loadMoreTopSales(this.page);
    } else {
      this.loadMoreWarehouse(this.page);
    }
  }

  checkWarehouseType(): void {
    this.userService
      .getWarehouseType(Number(localStorage.getItem(WAREHOUSE_ID_KEY)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ warehouses_type }) => {
          const type = Number(warehouses_type);

          if ([2, 3, 4].includes(type)) {
            this.router.navigate(['/Addorderstore']);
            return;
          }

          if (type === 1) {
            this.router.navigate(['/Addorderwarehouse']);
            return;
          }

          this.router.navigate(['/Addorder']);
        },
        error: (error) => this.handleError(error),
      });
  }

  toggleOffer(id: number): void {
    this.setLoadingState(true);

    this.homeService.toggleDailyOffer(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: this.isAr ? 'تم تحديث المنتج بنجاح' : 'Product updated successfully'
          });

          this.reloadCurrentMode();
        },
        error: (error) => this.handleError(error),
      });
  }

  private reloadCurrentMode(): void {
    if (this.mostSales) {
      this.getwarehouseBytopSale(1);
      return;
    }

    if (this.searched) {
      this.performSearch(false);
      return;
    }

    this.getwarehouse(this.warehouseId, 1);
  }

  changeQuantity(item: any): void {
    this.showRejectPopUp = true;
    this.itemId = item;
  }

  updateQtyFromStock(id: any, qty: any): void {
    this.setLoadingState(true);

    this.homeService.updateQtyFromStock(id, qty)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.reloadCurrentMode(),
        error: (error) => this.handleError(error),
      });
  }

  onSubmit(): void {
    const qty = Number(this.quantity);

    if (!this.quantity || isNaN(qty)) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'من فضلك ادخل رقم صحيح' : 'Please enter a valid number'
      });
      return;
    }

    if (qty < 0) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'الكمية لا يمكن أن تكون أقل من صفر' : 'Quantity cannot be negative'
      });
      return;
    }

    if (!Number.isInteger(qty)) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'الكمية لازم تكون رقم صحيح' : 'Quantity must be a whole number'
      });
      return;
    }

    if (this.itemId !== undefined && this.itemId !== null) {
      this.updateQtyFromStock(this.itemId, qty);
    }

    this.close();
  }

  deleteitem(id: string): void {
    Swal.fire({
      title: this.isAr ? 'متأكد؟؟' : 'Are You Sure?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'NO'
    }).then((result) => {
      if (result.isConfirmed) {
        this.setLoadingState(true);

        this.homeService.delete(id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.reloadCurrentMode(),
            error: (error) => this.handleError(error),
          });
      }
    });
  }

  openAddImage(id: any): void {
    this.stockImageId = id;
    this.addImage = true;
    this.imagePreview = null;
    this.selectedImageFile = null;
  }

  onImageSelected(event: any): void {
    const file = event.target.files?.[0];

    if (!file) return;

    this.selectedImageFile = file;

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  submitImage(): void {
    if (!this.selectedImageFile || !this.stockImageId) {
      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'من فضلك اختر صورة أولاً' : 'Please select an image first'
      });
      return;
    }

    this.isUploadingImage = true;

    this.homeService.updateStockImage(this.stockImageId, this.selectedImageFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isUploadingImage = false;

          Swal.fire({
            icon: 'success',
            title: this.isAr ? 'تم تحديث الصورة بنجاح' : 'Image updated successfully'
          });

          this.close();
          this.reloadCurrentMode();
        },
        error: () => {
          this.isUploadingImage = false;

          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'حدث خطأ أثناء تحديث الصورة' : 'An error occurred while updating the image'
          });
        },
      });
  }

  addToCart(item: any): void {
    if (this.toInt(item.quantity) === 0) {
      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'المنتج غير متوفر' : 'Out of stock'
      });
      return;
    }

    this.cartService.addOrToggleItem(item, 'product');
  }

  removeFromCart(item: any): void {
    this.cartService.removeItem(item);

    if (this.cartService.getCart().length === 0) {
      this.cartPopUp = false;
      localStorage.removeItem('cart');
      localStorage.removeItem('cartWarehouseId');
    }
  }

  isInCart(item: any): boolean {
    return this.cartItems.some(cartItem => cartItem.id === item.id && (cartItem.cartItemType || 'product') === 'product');
  }

  quickOrder(): void {
    const posId = Number(localStorage.getItem(WAREHOUSE_ID_KEY));

    this.userService.getWarehouseType(posId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ warehouses_type }) => {
          const type = Number(warehouses_type);

          if ([2, 3, 4].includes(type)) {
            this.router.navigate(['/Addorderstore'], {
              queryParams: {
                cart: JSON.stringify(this.cartItems)
              }
            });
            return;
          }

          if (type === 1) {
            this.router.navigate(['/Addorderwarehouse'], {
              queryParams: {
                cart: JSON.stringify(this.cartItems)
              }
            });
            return;
          }

          this.router.navigate(['/Addorder'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
        },
        error: (error) => this.handleError(error),
      });
  }

  cartPopUpOpen(): void {
    this.cartPopUp = true;
  }

  close(): void {
    this.showRejectPopUp = false;
    this.cartPopUp = false;
    this.addImage = false;

    this.quantity = undefined;
    this.itemId = undefined;
    this.stockImageId = null;
    this.imagePreview = null;
    this.selectedImageFile = null;
    this.isUploadingImage = false;
  }

  edit(itemobj: any): void {
    this.router.navigate(['/AddItem'], {
      queryParams: itemobj
    });
  }

  clone(itemobj: any): void {
    this.router.navigate(['/AddItem'], {
      queryParams: {
        ...itemobj,
        isClone: true
      }
    });
  }

  getDisplayName(element: any): string {
    return this.isAr ? element.name_ar : element.name_en;
  }

  getCategoryName(categoryId: any): string {
    return CATEGORY_NAMES[String(categoryId)] ?? '-';
  }

  getPetTypeName(petType: any): string {
    return PET_TYPE_NAMES[String(petType)] ?? '-';
  }

  getPetName(pet: any): string {
    return PET_NAMES[String(pet)] ?? '-';
  }

  formatDate(date: string): string {
    if (!date) return '-';

    const d = new Date(date);

    if (isNaN(d.getTime())) return '-';

    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(2);

    return `${day}-${month}-${year}`;
  }

  isLowStock(quantity: number): boolean {
    return Number(quantity) <= LOW_STOCK_THRESHOLD;
  }

  isExpiringSoon(date: string): boolean {
    if (!date) return false;

    const today = new Date();
    const inputDate = new Date(date);

    if (isNaN(inputDate.getTime())) return false;

    const diffInTime = inputDate.getTime() - today.getTime();
    const diffInMonths = diffInTime / (1000 * 3600 * 24 * 30);

    return diffInMonths < EXPIRATION_WARNING_MONTHS;
  }

  toInt(val: any): number {
    const parsed = Number.parseInt(String(val ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  fixImage(url: any): string {
    return IMAGE_BASE_URL + url;
  }

  isShopLike(): boolean {
    return this.warehouseTypeService.isShop() || this.warehouseTypeService.isShopAndClinic();
  }

  canShowPrice(columnKey: string): boolean {
    const isAdminLike = this.isAdmin || this.isSubAdmin;
    const isWarehouse = this.warehouseTypeService.isWarehouse();

    if (isAdminLike) {
      if (isWarehouse) {
        return columnKey === 'price';
      }

      if (this.isShopLike()) {
        return ['price', 'pricea'].includes(columnKey);
      }

      return ['price', 'affiliationprice', 'pricea', 'priceb', 'pricec'].includes(columnKey);
    }

    if (this.isShopLike()) {
      return ['price', 'pricea'].includes(columnKey);
    }

    const accountPriceMap: Record<string, string> = {
      '2': 'affiliationprice',
      '3': 'pricea',
      '4': 'priceb',
      '5': 'pricec'
    };

    return accountPriceMap[this.accountType] === columnKey;
  }

  getPriceColumnHeader(columnKey: string): string {
    if (this.isShopLike()) {
      const shopHeaders: Record<string, string> = {
        price: 'Wholesale Price',
        pricea: 'Selling price'
      };

      return shopHeaders[columnKey] ?? columnKey;
    }

    if (this.isAdmin || this.isSubAdmin) {
      const headers: Record<string, string> = {
        affiliationprice: 'Affiliation',
        pricea: 'Page A',
        priceb: 'B',
        pricec: 'C',
        price: 'price'
      };

      return headers[columnKey] ?? 'price';
    }

    return 'price';
  }

  getVisiblePrices(element: any): PriceLine[] {
    const keys = ['price', 'affiliationprice', 'pricea', 'priceb', 'pricec'];

    return keys
      .filter(key => this.canShowPrice(key))
      .map(key => ({
        key,
        label: this.getPriceColumnHeader(key),
        value: Number(element[key] ?? 0)
      }));
  }
}
